import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import { Alert, Badge, Button, FullScreenLoader } from '../../components/ui'
import { ApiError } from '../../api/client'
import { keys } from '../../api/keys'
import { fetchThreadScreen, markThreadRead } from '../../api/threads'
import type { Draft } from '../../api/threads'
import { t } from '../../i18n'
import { useSession } from '../../auth/session'
import { fullTime } from '../../format'
import { threadModeLabel } from '../../labels'
import { Conversation } from './Conversation'
import { ManualReply } from './ManualReply'
import { DraftCard } from './DraftCard'
import { ThreadModeControl } from './ThreadMode'
import { SearchRequestPanel } from './SearchRequestPanel'
import { TaskList } from './TaskList'

/**
 * One conversation: what was said, what the system understood from it, and what
 * it proposes to say next.
 *
 * The three are on one screen because they are judged together. A draft is only
 * approvable in the light of the request it was written from, and a value in
 * that request is only correctable next to the message it came from.
 */
export function ThreadScreen() {
  const { threadId } = useParams<{ threadId: string }>()
  const { can } = useSession()
  // Clicking a piece of evidence in the right-hand panel scrolls the message
  // that produced it into view. This is the whole reason the two panes share a
  // screen rather than being two pages.
  //
  // A count rides along with the id so that pressing the same link twice still
  // scrolls: with the id alone the second press set the state it already had,
  // React did nothing, and the link looked broken.
  const [highlight, setHighlight] = useState<{ readonly id: string; readonly n: number } | null>(null)
  const highlighted = highlight?.id ?? null
  const showMessage = (id: string) => setHighlight((previous) => ({ id, n: (previous?.n ?? 0) + 1 }))

  const screen = useQuery({
    queryKey: keys.thread(threadId ?? ''),
    queryFn: ({ signal }) => fetchThreadScreen(threadId ?? '', signal),
    enabled: threadId !== undefined,
  })

  // Opening a conversation is reading it. Until this call existed the count only
  // fell when a reply went out, so a conversation read and not answered stayed
  // bold for ever and the «Непрочитанные» tab never emptied.
  //
  // Once per conversation, not once per render: the effect is keyed on the id,
  // and a failure is ignored on purpose — a counter that did not clear is not
  // worth an error in front of somebody reading a message.
  const client = useQueryClient()
  useEffect(() => {
    if (threadId === undefined) return
    markThreadRead(threadId)
      .then(() => {
        void client.invalidateQueries({ queryKey: keys.anyThreadList() })
      })
      .catch(() => {})
  }, [threadId, client])

  if (threadId === undefined) return <Alert tone="error">{t('thread.notFound')}</Alert>
  if (screen.isPending) return <FullScreenLoader />

  if (screen.isError) {
    const notFound = screen.error instanceof ApiError && screen.error.status === 404
    return (
      <div className="thread__error">
        <Alert tone="error">{notFound ? t('thread.notFound') : t('thread.failed')}</Alert>
        {notFound ? null : (
          <Button
            variant="secondary"
            onClick={() => {
              void screen.refetch()
            }}
          >
            {t('common.retry')}
          </Button>
        )}
      </div>
    )
  }

  const { thread, contact, searchRequest, drafts, tasks, tasksTruncated } = screen.data
  // The newest sendable proposal leads. When there is none — the ordinary state
  // after the owner has answered by hand (docs/adr/0003) — the newest one still
  // leads, greyed and labelled, because "the owner already replied" is the
  // answer to "why did the assistant say nothing", and hiding it behind a
  // disclosure is how that question gets asked again tomorrow.
  const primary = drafts.find((draft) => draft.approvable) ?? drafts[0]

  return (
    <div className="thread">
      <header className="thread__head">
        <div className="thread__who">
          <h1 className="thread__name">{contact?.displayName ?? thread.contactId}</h1>
          {contact?.username === undefined ? null : (
            <span className="thread__username">@{contact.username}</span>
          )}
          {contact?.locale === undefined ? null : <Badge tone="muted">{contact.locale}</Badge>}
        </div>

        <div className="thread__state">
          {/*
            Paused is coloured like a handoff, not like the ordinary state. Both
            mean the same thing to somebody scanning: the assistant is not
            working on this one, and a person has to.
          */}
          <Badge
            tone={
              thread.mode === 'handoff_pending' || thread.mode === 'human_paused' ? 'warning' : 'muted'
            }
          >
            {threadModeLabel(thread.mode)}
          </Badge>
          {thread.unreadCount > 0 ? (
            <Badge tone="accent">{t('thread.unread', { count: thread.unreadCount })}</Badge>
          ) : null}
          {/*
            A conversation somebody let in by hand rather than one a rule
            admitted (docs/adr/0012). Marked here because everything else on
            this screen looks identical either way.
          */}
          {thread.addedAt === undefined ? null : (
            <Badge tone="muted" title={t('thread.addedByHandHint')}>
              {t('thread.addedByHand')}
            </Badge>
          )}
        </div>
      </header>

      {/*
        A conversation outside the scope filter is one the system deliberately
        does not store or answer (invariant 14). Saying so is the difference
        between a quiet screen and a broken one.
      */}
      {thread.inScope ? null : <Alert tone="warning">{t('thread.outOfScope')}</Alert>}

      {/*
        How far back what is on this screen actually goes. Without it a manager
        reading an imported conversation has no way to know the earlier messages
        are simply not here, and would tell a client something the part nobody
        imported contradicts.
      */}
      {thread.historyFrom === undefined ? null : (
        <Alert tone="info">
          {t('thread.historyFrom', { date: fullTime(thread.historyFrom) })}
        </Alert>
      )}

      {/*
        Above the messages, not beside them. A paused conversation looks exactly
        like a quiet one until something says otherwise, and the question it
        answers — why is the assistant proposing nothing here — is asked before
        the conversation is read, not after.
      */}
      <ThreadModeControl threadId={thread.id} mode={thread.mode} />

      {thread.humanActiveUntil === undefined ? null : (
        <Alert tone="info">{t('thread.humanActiveUntil', { time: fullTime(thread.humanActiveUntil) })}</Alert>
      )}

      <div className="thread__columns">
        <div className="thread__main">
          <section className="panel-block">
            <h2 className="panel-block__title">{t('thread.messagesTitle')}</h2>
            <Conversation threadId={threadId} highlighted={highlighted} highlightCount={highlight?.n ?? 0} />
          </section>

          <Drafts drafts={drafts} primary={primary} canRead={can('drafts.read')} />

          {/*
            Below the proposals, not above them: the assistant's answer is the
            one to read first, and writing your own is what you do when it is not
            the answer you wanted.
          */}
          <ManualReply threadId={threadId} />
        </div>

        <aside className="thread__side">
          <SearchRequestPanel request={searchRequest} onEvidence={showMessage} />
          <TaskList tasks={tasks} truncated={tasksTruncated} threadId={threadId} onEvidence={showMessage} />
        </aside>
      </div>
    </div>
  )
}

/**
 * The proposals, with the one that can actually be sent at the top.
 *
 * The rest are kept visible rather than hidden: "the owner already replied" and
 * "the client wrote again" are the two most common reasons an answer never went
 * out, and a manager who cannot see them assumes the assistant said nothing.
 */
function Drafts({
  drafts,
  primary,
  canRead,
}: {
  readonly drafts: readonly Draft[]
  readonly primary: Draft | undefined
  readonly canRead: boolean
}) {
  const others = drafts.filter((draft) => draft.id !== primary?.id)

  return (
    <section className="panel-block">
      <h2 className="panel-block__title">{t('drafts.title')}</h2>

      {!canRead ? (
        <Alert tone="info">{t('drafts.forbidden')}</Alert>
      ) : drafts.length === 0 ? (
        <p className="panel-block__empty">{t('drafts.empty')}</p>
      ) : null}

      {/*
        Keyed on the draft, not on its position. Without this React kept one
        component instance while the row underneath it changed: a client wrote
        again, the pipeline retired the draft and wrote a new one, and the half-
        finished edit stayed in the box as if it belonged to the new proposal --
        which the manager then approved, answering the message before last. The
        version guard could not catch it, because every pending draft is version 1.
      */}
      {primary === undefined ? null : <DraftCard key={primary.id} draft={primary} primary />}

      {others.length === 0 ? null : (
        <details className="draft-history">
          <summary className="draft-history__summary">
            {t('drafts.titleOthers', { count: others.length })}
          </summary>
          <div className="draft-history__list">
            {others.map((draft) => (
              <DraftCard key={draft.id} draft={draft} primary={false} />
            ))}
          </div>
        </details>
      )}
    </section>
  )
}
